/**
 * The partner-config panel (epic 085 D9, task 04): one `Banner` per issue the
 * server's check returned, in the order it returned them (blockers first).
 * Red for a setup that can't be saved, red "Already saved this way" for a
 * grandfathered one (D4 — shown, never gating), amber for a warning, blue for
 * a note. `null` (no answer / a failed check) and an empty list render nothing.
 */

import type { PartnerConfigIssue } from '@leads-platform/shared';
import { Banner } from '../../components/ui/banner';

type IssueTone = 'stop' | 'warn' | 'note';

interface IssuePresentation {
  tone: IssueTone;
  title: string;
}

const GRANDFATHERED: IssuePresentation = { tone: 'stop', title: 'Already saved this way:' };

const BY_SEVERITY = {
  block: { tone: 'stop', title: "Can't save:" },
  warn: { tone: 'warn', title: 'Check:' },
  info: { tone: 'note', title: 'Note:' },
} satisfies Record<PartnerConfigIssue['severity'], IssuePresentation>;

function presentationOf(issue: PartnerConfigIssue): IssuePresentation {
  if (issue.severity === 'block' && issue.existing === true) return GRANDFATHERED;
  return BY_SEVERITY[issue.severity];
}

/** The server prefixes a grandfathered message with the title — show it once. */
function bodyOf(issue: PartnerConfigIssue, title: string): string {
  return issue.message.startsWith(title)
    ? issue.message.slice(title.length).trimStart()
    : issue.message;
}

interface PartnerConfigIssuesProps {
  issues: PartnerConfigIssue[] | null;
  /** Only the issues anchored at this form field (e.g. `suppression.prepingApiUrl`). */
  field?: string;
}

export function PartnerConfigIssues({ issues, field }: PartnerConfigIssuesProps) {
  const shown =
    issues === null
      ? []
      : field === undefined
        ? issues
        : issues.filter((issue) => issue.field === field);
  if (shown.length === 0) return null;
  return (
    <div data-testid="partner-config-issues" role="status" className="space-y-2">
      {shown.map((issue, index) => {
        const { tone, title } = presentationOf(issue);
        return (
          <Banner key={`${issue.code}-${index}`} tone={tone} title={title}>
            {bodyOf(issue, title)}
          </Banner>
        );
      })}
    </div>
  );
}
